import Information16 from '@carbon/icons-react/es/information/16';
import { Tooltip } from 'antd';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ProjectId } from 'api/models';
import {
  getAggregatedPhaseProgress,
  getProgress,
  ProgressStatusBar,
} from 'components/molecules/projects/progress-status';
import { HelperText01, Text03 } from 'components/styles';
import { useProjectDetail } from 'data/projects';
import { useTranslation } from 'hooks/use-translation';
import { useIsExternalUser } from 'hooks/use-user-type';
import { EmptyParagraph } from '../../../style';

const Space = styled.div`
  align-items: center;
  display: flex;
  height: var(--spacing-06);
  margin-bottom: var(--spacing-03);
  justify-content: space-between;

  h5 {
    margin: 0 var(--layout-02) 0 0;
  }
`;

const Title = styled.p`
  align-content: center;
  display: flex;
  ${HelperText01};
  flex: 1;
  margin: 0;

  .icon {
    margin-left: var(--spacing-03);
  }
`;

const HelpText = styled.p`
  ${HelperText01};
  ${Text03};
  margin: 0;
`;

export const ProjectOverviewHighlightTileProgress: FC<{
  projectId: number;
}> = ({ projectId }) => {
  const isExternalUser = useIsExternalUser();
  const { data: project } = useProjectDetail(ProjectId(projectId));
  const progressStatus = getAggregatedPhaseProgress(
    project?.projectPhases ?? [],
  );
  const completionTitle = useTranslation('project.overview.completion.title');

  const { completed, completedPercent, total } = getProgress(progressStatus);

  return (
    <>
      <Space>
        <Title>
          <FormattedMessage id="project.overview.completion.title" />
          <Tooltip
            title={
              <FormattedMessage id="project.overview.completion.tooltip" />
            }
          >
            <Information16 className="icon" />
          </Tooltip>
        </Title>
        {(total > 0 || isExternalUser) && (
          <HelpText>
            <FormattedMessage
              id="project.overview.completion.help_text"
              values={{ total, completed }}
            />
          </HelpText>
        )}
      </Space>
      {total > 0 || isExternalUser ? (
        <Space>
          <h5>{completedPercent}%</h5>
          <ProgressStatusBar
            progressStatuses={progressStatus}
            showPopover
            strokeWidth={24}
            title={completionTitle}
          />
        </Space>
      ) : (
        <EmptyParagraph>
          <FormattedMessage id="project.overview.progress.empty" />
          <Link
            to={`/projects/${projectId}/tasks`}
            data-cy="create-new-task-cta"
          >
            <FormattedMessage id="project.overview.progress.empty.cta" />
          </Link>
        </EmptyParagraph>
      )}
    </>
  );
};
